import React, { ReactNode } from 'react';
import { Typography, Box } from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import { IconVerticalLogo } from '../../../../utils/icons';
import { WhiteBackground } from '../../../../components/WhiteBackground';
import { MainContainer } from '../../../../components/ContentWrapper';
import { GoToPortalLink } from './helper';
import { useUser } from '../../../../hooks/useUser';
import { useNoIndexMeta } from '../../../../hooks/useNoIndexMeta';
import portalHeader from '../../../../assets/portalHeader.png';

interface PublicShareContainerProps {
  title: string;
  descriptionContent?: ReactNode;
  children: ReactNode;
  // When true, skip the constrained MainContainer wrap so children render
  // edge-to-edge (e.g. for pages that paint their own full-width background).
  unwrap?: boolean;
}

export const PublicShareContainer: React.FC<PublicShareContainerProps> = ({
  title,
  descriptionContent,
  children,
  unwrap = false,
}) => {
  useNoIndexMeta();

  const navigate = useNavigate();
  const { user } = useUser();
  const isLoggedIn = !!user;

  return (
    <WhiteBackground>
      <div className="flex flex-col min-h-screen justify-between">
        <div className="flex flex-col">
          <div className="flex flex-col">
            <div
              className="relative h-[128px] md:h-[164px] w-full max-sm:h-[200px]"
              style={{
                backgroundImage: `url(${portalHeader})`,
                backgroundSize: 'contain',
              }}
            >
              {!isLoggedIn && (
                <Link
                  to="/login"
                  className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10"
                  style={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontFamily: 'CircularStd',
                    fontSize: '16px',
                    fontWeight: 500,
                    textDecoration: 'none',
                  }}
                >
                  Login
                </Link>
              )}
              <div className="absolute flex flex-col sm:flex-row items-center justify-around right-0 left-0 top-0 bottom-0 bg-[#00000075] max-sm:px-4 max-sm:gap-0 max-sm:justify-evenly">
                <Box
                  onClick={() => navigate('/')}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 0.8,
                    },
                    '@media (max-width: 599.95px)': {
                      '& svg': {
                        width: 90,
                        height: 'auto',
                      },
                    },
                  }}
                >
                  <IconVerticalLogo />
                </Box>
                <div className="max-sm:text-center max-sm:min-w-0">
                  <Typography
                    variant="body1"
                    fontSize={40}
                    fontWeight={900}
                    fontFamily="CircularStdBlack"
                    textTransform="uppercase"
                    color="#fff"
                    sx={{
                      '@media (max-width: 599.95px)': {
                        fontSize: 22,
                        lineHeight: 1.15,
                        wordBreak: 'break-word',
                      },
                    }}
                  >
                    {title}
                  </Typography>
                  <GoToPortalLink isLoggedIn={isLoggedIn} />
                </div>
              </div>
            </div>
            {descriptionContent && (
              <div className="flex flex-col py-6 px-10 gap-4 bg-surface-2 max-sm:py-4 max-sm:px-4 max-sm:gap-3">
                {descriptionContent}
              </div>
            )}
          </div>
          {unwrap ? children : <MainContainer>{children}</MainContainer>}
        </div>
        <div className="bg-black relative h-[128px] md:h-[164px] flex flex-col items-center justify-center w-full bg-[url(/src/assets/svg/half-logo-golden-left.svg)] bg-contain gap-4 max-sm:gap-3 max-sm:px-4">
          <Typography
            variant="h6"
            fontSize={20}
            fontFamily="CircularStd"
            color="#fff"
            textTransform="uppercase"
            className="border-b"
            sx={{
              '@media (max-width: 599.95px)': {
                fontSize: 14,
                textAlign: 'center',
                wordBreak: 'break-word',
              },
            }}
          >
            Sales@TheDesirecompany.com
          </Typography>
          <GoToPortalLink isLoggedIn={isLoggedIn} />
        </div>
      </div>
    </WhiteBackground>
  );
};
